import { MutationFunction } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  GetTeamMembersQuery,
  GroupRole,
  TransferOwnershipMutation,
  TransferOwnershipMutationVariables,
  UpdateTeamMemberMutation,
  UpdateTeamMemberMutationVariables,
} from '@/__generated__/graphql';
import ERROR_CODES from '@/core/apiErrorCodes';

import { ActivityGuardError, handleMutationError } from './handleMutationError';

export type TeamMember = NonNullable<
  NonNullable<GetTeamMembersQuery['groupMembers']>['edges'][number]
>['node'];

type Members = { edges: ReadonlyArray<{ node: TeamMember }> };

type Team = {
  has_owner?: boolean | null;
  billing_account?: { isSMBAccount?: boolean | null } | null;
};

type NotifyFn = (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
type FetchGroupFn = (id: string, opts?: { subscription?: boolean }) => Promise<Team | undefined>;
type RaceOpts = { retried?: boolean };

type OwnershipBroadcast = { type: 'ownership-changed'; groupId: string };

export interface TransferTarget {
  id: string;
  name: string;
  role: GroupRole;
}

// BroadcastChannel name shared across same-origin tabs so that an ownership
// change in one tab can prompt others to refresh their Apollo cache. We rely
// on the server-side guards (ErrTeamAlreadyHasOwner / ErrNoCurrentOwner) for
// correctness; this only improves UI freshness.
const OWNERSHIP_BROADCAST_CHANNEL = 'of-team-ownership';

export interface UseOwnershipActionsArgs {
  groupId: string;
  team: Team;
  members: Members;
  notify: NotifyFn;
  fetchGroup: FetchGroupFn;
  refreshSeatAllocation: () => void;
  setActivityGuardError: (guard: ActivityGuardError | null) => void;
  updateTeamMember: MutationFunction<UpdateTeamMemberMutation, UpdateTeamMemberMutationVariables>;
  transferOwnership: MutationFunction<
    TransferOwnershipMutation,
    TransferOwnershipMutationVariables
  >;
  transferLoading: boolean;
}

export function useOwnershipActions(args: UseOwnershipActionsArgs) {
  const {
    groupId,
    team,
    members,
    notify,
    fetchGroup,
    refreshSeatAllocation,
    setActivityGuardError,
    updateTeamMember,
    transferOwnership,
    transferLoading,
  } = args;

  const isSMB = !!team?.billing_account?.isSMBAccount;
  const hasOwner = team?.has_owner;

  const [transferTarget, setTransferTarget] = useState<TransferTarget | null>(null);

  // Best-effort post-mutation refresh: refetches the team and seat allocation
  // so cached fields like `has_owner` and `seatAllocation.used` reflect the
  // new state. Failures are non-critical (next navigation will refresh) but
  // are reported to Sentry so they don't disappear silently. `fetchGroup` is
  // async, so we await it — otherwise the surrounding try/catch can't trap
  // its rejection.
  const refreshAfterOwnershipChange = useCallback(async () => {
    try {
      refreshSeatAllocation();
    } catch (err) {
      Sentry.captureException(err, { level: 'warning' });
    }
    try {
      await fetchGroup(groupId);
    } catch (err) {
      Sentry.captureException(err, { level: 'warning' });
    }
  }, [refreshSeatAllocation, fetchGroup, groupId]);

  // Single same-tab BroadcastChannel reused by broadcaster and listener. A
  // BC instance doesn't echo to itself, so it's safe to both post and receive
  // on the same channel. Failures (e.g. jsdom, old browsers) are swallowed —
  // the server-side guards remain authoritative.
  const channelRef = useRef<BroadcastChannel | null>(null);

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    let channel: BroadcastChannel;
    try {
      channel = new BroadcastChannel(OWNERSHIP_BROADCAST_CHANNEL);
    } catch (err) {
      Sentry.captureException(err, { level: 'warning' });
      return undefined;
    }
    channelRef.current = channel;
    channel.onmessage = (ev: MessageEvent) => {
      const data = ev?.data;
      if (typeof data !== 'object' || data === null) return;
      const msg = data as Partial<OwnershipBroadcast>;
      if (msg.type !== 'ownership-changed' || msg.groupId !== groupId) return;
      refreshAfterOwnershipChange();
    };
    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [groupId, refreshAfterOwnershipChange]);

  const broadcastOwnershipChange = () => {
    const channel = channelRef.current;
    if (!channel) return;
    try {
      channel.postMessage({ type: 'ownership-changed', groupId } satisfies OwnershipBroadcast);
    } catch (err) {
      Sentry.captureException(err, { level: 'warning' });
    }
  };

  const performTransferOwnership = async (
    memberId: string,
    callerRole: GroupRole,
    opts: RaceOpts = {}
  ) => {
    try {
      await transferOwnership({
        variables: { groupId, memberId, callerRole },
      });
    } catch (err) {
      await handleMutationError(
        err,
        {
          onGuard: (guard) => {
            setTransferTarget(null);
            setActivityGuardError(guard);
          },
          codeMap: {
            [ERROR_CODES.TEAM_CANNOT_TRANSFER_TO_SELF]: () =>
              notify('You cannot transfer ownership to yourself.', 'error'),
            [ERROR_CODES.TEAM_CANNOT_TRANSFER_TO_PENDING]: () =>
              notify('Cannot transfer ownership to a pending member.', 'error'),
            [ERROR_CODES.TEAM_OWNER_MUST_TRANSFER_FIRST]: () =>
              notify('The current owner must transfer ownership first.', 'error'),
            [ERROR_CODES.TEAM_NO_OWNER_USE_ASSIGN]: async () => {
              // Race: owner was removed between page-load and click. Refetch,
              // then re-dispatch as initial assign instead of asking the user
              // to retry manually.
              setTransferTarget(null);
              await resolveOwnerRaceAsAssign(memberId, opts);
            },
          },
          fallback: 'Failed to transfer ownership',
        },
        notify
      );
      return;
    }

    setTransferTarget(null);
    notify('Ownership transferred successfully', 'success');
    refreshAfterOwnershipChange();
    broadcastOwnershipChange();
  };

  const performInitialOwnerAssignment = async (member: TeamMember, opts: RaceOpts = {}) => {
    try {
      await updateTeamMember({ variables: { id: member.id, role: GroupRole.Owner } });
      notify('Owner assigned successfully', 'success');
      refreshAfterOwnershipChange();
      broadcastOwnershipChange();
    } catch (err) {
      await handleMutationError(
        err,
        {
          onGuard: setActivityGuardError,
          codeMap: {
            [ERROR_CODES.BILLING_SEATS_EXHAUSTED]: () => notify('No seats available.', 'error'),
            [ERROR_CODES.TEAM_ALREADY_HAS_OWNER]: () =>
              // Race: another action assigned an owner first. Refetch and
              // re-dispatch as a transfer (SMB → confirmation dialog;
              // non-SMB → direct transfer) instead of asking the user to retry.
              resolveOwnerRaceAsTransfer(member, opts),
            [ERROR_CODES.TEAM_CANNOT_TRANSFER_TO_PENDING]: () =>
              notify('Cannot assign ownership to a pending invitee.', 'error'),
          },
          fallback: 'Failed to assign owner',
        },
        notify
      );
    }
  };

  // Race resolution: initial-assign saw an owner was just created.
  // Refetch and resume as a transfer if the owner is still present.
  const resolveOwnerRaceAsTransfer = async (member: TeamMember, opts: RaceOpts) => {
    if (opts.retried) {
      notify('Could not assign Owner — please refresh and try again.', 'error');
      return;
    }
    let freshTeam: Team | undefined;
    try {
      freshTeam = await fetchGroup(groupId);
    } catch (err) {
      Sentry.captureException(err, { level: 'warning' });
      notify('Could not refresh team state — please try again.', 'error');
      return;
    }
    // Refetch may resolve to undefined (NOT_FOUND, viewer permission changed)
    // or has_owner: null (viewer lost view_members). Bail with a refresh ask
    // instead of falling through to the wrong branch and a misleading toast.
    if (!freshTeam || typeof freshTeam.has_owner !== 'boolean') {
      notify('Could not refresh team state — please try again.', 'error');
      return;
    }
    if (freshTeam.has_owner === false) {
      // Edge: owner appeared and was already removed again. Retry once as
      // initial assign; the `retried` flag prevents further loops.
      await performInitialOwnerAssignment(member, { retried: true });
      return;
    }
    if (freshTeam.billing_account?.isSMBAccount) {
      setTransferTarget({
        id: member.id,
        name: member.user?.name || member.email || 'Unknown',
        role: member.role,
      });
    } else {
      await performTransferOwnership(member.id, GroupRole.Member, { retried: true });
    }
  };

  // Race resolution: transfer saw that the owner was removed.
  // Refetch and resume as an initial assign if the team is still ownerless.
  const resolveOwnerRaceAsAssign = async (memberId: string, opts: RaceOpts) => {
    if (opts.retried) {
      notify('Could not transfer ownership — please refresh and try again.', 'error');
      return;
    }
    let freshTeam: Team | undefined;
    try {
      freshTeam = await fetchGroup(groupId);
    } catch (err) {
      Sentry.captureException(err, { level: 'warning' });
      notify('Could not refresh team state — please try again.', 'error');
      return;
    }
    if (!freshTeam || typeof freshTeam.has_owner !== 'boolean') {
      notify('Could not refresh team state — please try again.', 'error');
      return;
    }
    if (freshTeam.has_owner === true) {
      // Edge: owner returned. Retry transfer once.
      await performTransferOwnership(memberId, GroupRole.Member, { retried: true });
      return;
    }
    const target = members.edges.find(({ node: m }) => m.id === memberId)?.node;
    if (!target?.user) {
      notify('Could not retry — please refresh and try again.', 'error');
      return;
    }
    await performInitialOwnerAssignment(target, { retried: true });
  };

  // Public: route based on team state. Branches:
  //   - has_owner === false → initial owner assignment
  //   - has_owner === true and SMB → confirmation dialog (billing implications)
  //   - has_owner === true and non-SMB → direct transfer (no billing impact)
  //   - has_owner === null (viewer lacks view_members) → fall through to
  //     transfer flow; the API guards either way.
  const promoteToOwner = async (member: TeamMember) => {
    if (!member.user) {
      notify('Cannot assign ownership to a pending invitee.', 'error');
      return;
    }
    if (hasOwner === false) {
      await performInitialOwnerAssignment(member);
    } else if (isSMB) {
      setTransferTarget({
        id: member.id,
        name: member.user.name || member.email || 'Unknown',
        role: member.role,
      });
    } else {
      await performTransferOwnership(member.id, GroupRole.Member);
    }
  };

  const handleTransferConfirm = async () => {
    if (!transferTarget) return;
    const callerRole = isSMB ? transferTarget.role : GroupRole.Member;
    await performTransferOwnership(transferTarget.id, callerRole);
  };

  return {
    transferTarget,
    setTransferTarget,
    transferLoading,
    promoteToOwner,
    handleTransferConfirm,
  };
}
